import React, { useState, useEffect } from 'react';
import { Nav, Button, Collapse, NavItem } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../../../api/session';
import {
  House,
  LayoutDashboard,
  Calendar,
  User,
  Printer,
  LogOut,
  Menu,
  Settings,
  ChevronDown,
  KeyRound,
  FileText,
  TrendingUp,
  TrendingDown,
  Bell,
  Star,
  BadgeDollarSign,
  Receipt,
  ClipboardClock,
  CircleCheck,
  SlidersHorizontal,
  MessageCircle,
  Moon,
  Sun,
  ShieldCheck,
  Users,
  UserCog,
  Smartphone,
  FolderOpen,
  Building2,
  ListTodo,
  BarChart3,
  Stethoscope,
  FlaskConical,
} from 'lucide-react';
import { useThemeContext } from '../../../context/ThemeContext';
import { APP_VERSION } from '../../../version';
import logoSigo from '../../../assets/logo_sigo.png';

export default function Sidebar({ onSelect, isOpen, toggleSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode, toggleDarkMode } = useThemeContext();

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFinancialOpen, setIsFinancialOpen] = useState(false);
  const [isAppointmentsOpen, setIsAppointmentsOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [hasFinancialRole, setHasFinancialRole] = useState(false);
  const [isClinicAdmin, setIsClinicAdmin] = useState(false);
  const [hasTasksRole, setHasTasksRole] = useState(false);

  useEffect(() => {
    const storedRoles = JSON.parse(sessionStorage.getItem('roles'));
    if (storedRoles) {
      setIsUserAdmin(storedRoles.includes('ADMIN'));
      setHasFinancialRole(
        storedRoles.includes('FINANCIAL') || storedRoles.includes('CLINIC_ADMIN'),
      );
      setIsClinicAdmin(storedRoles.includes('CLINIC_ADMIN'));
      setHasTasksRole(storedRoles.includes('RECEPTIONIST') || storedRoles.includes('CLINIC_ADMIN'));
    } else {
      setIsUserAdmin(false);
      setHasFinancialRole(false);
      setIsClinicAdmin(false);
      setHasTasksRole(false);
    }
  }, []);

  return (
    <div
      className="d-flex flex-column vh-100 shadow-sm position-relative bg-body-tertiary"
      style={{
        width: isOpen ? '220px' : '60px',
        transition: 'width 0.3s',
      }}
    >
      {/* Botão de toggle + logo */}
      <div className="d-flex align-items-center gap-2" style={{ padding: '10px' }}>
        <Button
          variant={darkMode ? 'dark' : 'light'}
          size="sm"
          onClick={toggleSidebar}
          aria-label={isOpen ? 'Recolher menu' : 'Expandir menu'}
          style={{ backgroundColor: darkMode ? 'var(--bs-secondary-bg)' : '#e6f2ff' }}
        >
          <Menu size={20} />
        </Button>
        {isOpen && (
          <button
            type="button"
            onClick={() => navigate('/home')}
            aria-label="Ir para o início"
            style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer' }}
          >
            <img src={logoSigo} alt="SIGO" style={{ height: '40px', objectFit: 'contain' }} />
          </button>
        )}
      </div>

      {/* Menu */}
      <Nav className="flex-column px-2 mt-3" variant="pills" activeKey={location.pathname}>
        <Nav.Item>
          <Nav.Link
            eventKey="/home"
            onClick={() => navigate('/home')}
            className="d-flex align-items-center gap-2 text-body"
          >
            <House size={20} />
            {isOpen && 'Início'}
          </Nav.Link>
        </Nav.Item>

        {isClinicAdmin && (
          <Nav.Item>
            <Nav.Link
              eventKey="/dashboard"
              onClick={() => navigate('/dashboard')}
              className="d-flex align-items-center gap-2 text-body"
            >
              <LayoutDashboard size={20} />
              {isOpen && 'Dashboard'}
            </Nav.Link>
          </Nav.Item>
        )}

        <Nav.Item>
          <Nav.Link
            active={location.pathname === '/agenda' || location.pathname === '/agenda-room'}
            onClick={() => navigate('/agenda-router')}
            className="d-flex align-items-center gap-2 text-body"
          >
            <Calendar size={20} />
            {isOpen && 'Agenda'}
          </Nav.Link>
        </Nav.Item>

        <Nav.Item>
          <Nav.Link
            eventKey="appointments"
            onClick={() => setIsAppointmentsOpen(!isAppointmentsOpen)}
            aria-controls="appointments-collapse"
            aria-expanded={isAppointmentsOpen}
            className="d-flex align-items-center gap-2 text-body"
            style={{ cursor: 'pointer' }}
          >
            <ClipboardClock size={20} />
            {isOpen && (
              <span className="d-flex justify-content-between w-100 align-items-center">
                Consultas
                <ChevronDown
                  size={16}
                  style={{
                    transform: isAppointmentsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    color: '#6c757d',
                  }}
                />
              </span>
            )}
          </Nav.Link>
        </Nav.Item>

        <Collapse in={isOpen && isAppointmentsOpen}>
          <div id="appointments-collapse">
            <Nav.Item>
              <Nav.Link
                eventKey="/appointments"
                onClick={() => navigate('/appointments')}
                className="d-flex align-items-center gap-2 text-body ms-3"
              >
                <CircleCheck size={20} />
                {isOpen && 'Confirmar'}
              </Nav.Link>
            </Nav.Item>
          </div>
        </Collapse>

        <Nav.Item>
          <Nav.Link
            eventKey="/patient-list"
            onClick={() => navigate('/patient-list')}
            className="d-flex align-items-center gap-2 text-body"
          >
            <User size={20} />
            {isOpen && 'Paciente'}
          </Nav.Link>
        </Nav.Item>

        <Nav.Item>
          <Nav.Link
            eventKey="/cadastros"
            onClick={() => navigate('/cadastros')}
            className="d-flex align-items-center gap-2 text-body"
          >
            <FolderOpen size={20} />
            {isOpen && 'Cadastros'}
          </Nav.Link>
        </Nav.Item>

        <Nav.Item>
          <Nav.Link
            data-cy="nav-laboratory"
            eventKey="/laboratory"
            onClick={() => navigate('/laboratory')}
            className="d-flex align-items-center gap-2 text-body"
          >
            <FlaskConical size={20} />
            {isOpen && 'Laboratório'}
          </Nav.Link>
        </Nav.Item>

        <Nav.Item>
          {hasTasksRole && (
            <Nav.Link
              eventKey="/tasks"
              onClick={() => navigate('/tasks')}
              className="d-flex align-items-center gap-2 text-body"
            >
              <ListTodo size={20} />
              {isOpen && 'Tarefas'}
            </Nav.Link>
          )}
        </Nav.Item>

        <Nav.Item>
          {hasFinancialRole && (
            <Nav.Link
              data-cy="nav-financial"
              eventKey="financial"
              onClick={() => setIsFinancialOpen(!isFinancialOpen)}
              aria-controls="financial-collapse"
              aria-expanded={isFinancialOpen}
              className="d-flex align-items-center gap-2 text-body"
              style={{ cursor: 'pointer' }}
            >
              <BadgeDollarSign size={20} />
              {isOpen && (
                <span className="d-flex justify-content-between w-100 align-items-center">
                  Financeiro
                  <ChevronDown
                    size={16}
                    style={{
                      transform: isFinancialOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      color: '#6c757d',
                    }}
                  />
                </span>
              )}
            </Nav.Link>
          )}
        </Nav.Item>

        <Collapse in={isOpen && isFinancialOpen}>
          <div id="financial-collapse">
            <Nav.Item>
              <Nav.Link
                data-cy="nav-financial-dashboard"
                eventKey="/financial-dashboard"
                onClick={() => navigate('/financial-dashboard')}
                className="d-flex align-items-center gap-2 text-body ms-3"
              >
                <Receipt size={20} />
                {isOpen && 'Dashboard'}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                data-cy="nav-incomes"
                eventKey="/financial/incomes"
                onClick={() => navigate('/financial/incomes')}
                className="d-flex align-items-center gap-2 text-body ms-3"
              >
                <TrendingUp size={20} />
                {isOpen && 'Entradas'}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                data-cy="nav-expenses"
                eventKey="/financial/expenses"
                onClick={() => navigate('/financial/expenses')}
                className="d-flex align-items-center gap-2 text-body ms-3"
              >
                <TrendingDown size={20} />
                {isOpen && 'Saídas'}
              </Nav.Link>
            </Nav.Item>
          </div>
        </Collapse>

        <Nav.Item>
          {isClinicAdmin && (
            <Nav.Link
              data-cy="nav-reports"
              eventKey="reports"
              onClick={() => setIsReportsOpen(!isReportsOpen)}
              aria-controls="reports-collapse"
              aria-expanded={isReportsOpen}
              className="d-flex align-items-center gap-2 text-body"
              style={{ cursor: 'pointer' }}
            >
              <BarChart3 size={20} />
              {isOpen && (
                <span className="d-flex justify-content-between w-100 align-items-center">
                  Relatórios
                  <ChevronDown
                    size={16}
                    style={{
                      transform: isReportsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      color: '#6c757d',
                    }}
                  />
                </span>
              )}
            </Nav.Link>
          )}
        </Nav.Item>

        <Collapse in={isOpen && isReportsOpen}>
          <div id="reports-collapse">
            <Nav.Item>
              <Nav.Link
                data-cy="nav-clinical-production"
                eventKey="/reports/clinical-production"
                onClick={() => navigate('/reports/clinical-production')}
                className="d-flex align-items-center gap-2 text-body ms-3"
              >
                <Stethoscope size={20} />
                {isOpen && 'Produção clínica'}
              </Nav.Link>
            </Nav.Item>
          </div>
        </Collapse>

        {isClinicAdmin && (
          <Nav.Item>
            <Nav.Link
              data-cy="nav-sent-messages"
              eventKey="/sent-messages"
              onClick={() => navigate('/sent-messages')}
              className="d-flex align-items-center gap-2 text-body"
            >
              <MessageCircle size={20} />
              {isOpen && 'Mensagens Enviadas'}
            </Nav.Link>
          </Nav.Item>
        )}

        <Nav.Item>
          <Nav.Link
            eventKey="impressoes"
            onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            aria-controls="settings-collapse"
            aria-expanded={isSettingsOpen}
            className="d-flex align-items-center gap-2 text-body"
            style={{ cursor: 'pointer' }}
          >
            <Settings size={20} />
            {isOpen && (
              <span className="d-flex justify-content-between w-100 align-items-center">
                Configurações
                <ChevronDown
                  size={16}
                  style={{
                    transform: isSettingsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    color: '#6c757d',
                  }}
                />
              </span>
            )}
          </Nav.Link>
        </Nav.Item>

        <Collapse in={isOpen && isSettingsOpen}>
          <div id="settings-collapse">
            {isClinicAdmin && (
              <>
                <Nav.Item>
                  <Nav.Link
                    eventKey="/settings/general"
                    onClick={() => navigate('/settings/general')}
                    className="d-flex align-items-center gap-2 text-body ms-3"
                  >
                    <SlidersHorizontal size={20} />
                    {isOpen && 'Gerais'}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="/settings/clinic"
                    onClick={() => navigate('/settings/clinic')}
                    className="d-flex align-items-center gap-2 text-body ms-3"
                  >
                    <Building2 size={20} />
                    {isOpen && 'Clínica'}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="/print-settings"
                    onClick={() => navigate('/print-settings')}
                    className="d-flex align-items-center gap-2 text-body ms-3"
                  >
                    <Printer size={20} />
                    {isOpen && 'Impressões'}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="/configuration/users"
                    onClick={() => navigate('/configuration/users')}
                    className="d-flex align-items-center gap-2 text-body ms-3"
                  >
                    <UserCog size={20} />
                    {isOpen && 'Usuários e acessos'}
                  </Nav.Link>
                </Nav.Item>
              </>
            )}
            <Nav.Item>
              <Nav.Link
                eventKey="/change-password"
                onClick={() => navigate('/change-password')}
                className="d-flex align-items-center gap-2 text-body ms-3"
              >
                <KeyRound size={20} />
                {isOpen && 'Alterar Senha'}
              </Nav.Link>
            </Nav.Item>
            <NavItem>
              <Nav.Link
                eventKey="/terms"
                onClick={() => navigate('/terms')}
                className="d-flex align-items-center gap-2 text-body ms-3"
              >
                <FileText size={20} />
                {isOpen && 'Termos'}
              </Nav.Link>
            </NavItem>
          </div>
        </Collapse>

        {isUserAdmin && (
          <>
            <Nav.Item>
              <Nav.Link
                eventKey="administrativo"
                onClick={() => setIsAdminOpen(!isAdminOpen)}
                aria-controls="admin-collapse"
                aria-expanded={isAdminOpen}
                className="d-flex align-items-center gap-2 text-body"
                style={{ cursor: 'pointer' }}
              >
                <ShieldCheck size={20} />
                {isOpen && (
                  <span className="d-flex justify-content-between w-100 align-items-center">
                    Administrativo
                    <ChevronDown
                      size={16}
                      style={{
                        transform: isAdminOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        color: '#6c757d',
                      }}
                    />
                  </span>
                )}
              </Nav.Link>
            </Nav.Item>

            <Collapse in={isOpen && isAdminOpen}>
              <div id="admin-collapse">
                <Nav.Item>
                  <Nav.Link
                    eventKey="/settings/notifications"
                    onClick={() => navigate('/settings/notifications')}
                    className="d-flex align-items-center gap-2 text-body ms-3"
                  >
                    <Bell size={20} />
                    {isOpen && 'Notificações'}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="/settings/access"
                    onClick={() => navigate('/settings/access')}
                    className="d-flex align-items-center gap-2 text-body ms-3"
                  >
                    <Users size={20} />
                    {isOpen && 'Acessos'}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="/settings/messagesent"
                    onClick={() => navigate('/settings/messagesent')}
                    className="d-flex align-items-center gap-2 text-body ms-3"
                  >
                    <Smartphone size={20} />
                    {isOpen && 'Mensagens Enviadas'}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="/nps"
                    onClick={() => navigate('/nps')}
                    className="d-flex align-items-center gap-2 text-body ms-3"
                  >
                    <Star size={20} />
                    {isOpen && 'NPS'}
                  </Nav.Link>
                </Nav.Item>
              </div>
            </Collapse>
          </>
        )}

        {/* Menu desabilitado temporariamente
        <Nav.Item>
          <Nav.Link
            eventKey="/marketing"
            onClick={() => navigate('/marketing')}
            className="d-flex align-items-center gap-2 text-body"
          >
            <Megaphone size={20} />
            {isOpen && 'Marketing'}
          </Nav.Link>
        </Nav.Item>
        */}

        <Nav.Item>
          <Nav.Link
            eventKey="suporte"
            onClick={() => window.open('https://wa.me/5548991094459', '_blank')}
            className="d-flex align-items-center gap-2 text-body"
          >
            <MessageCircle size={20} />
            {isOpen && 'Suporte'}
          </Nav.Link>
        </Nav.Item>

        {/* Menu desabilitado temporariamente
        <Nav.Item>
          <Nav.Link
            eventKey="/benefits"
            onClick={() => navigate('/benefits')}
            className="d-flex align-items-center gap-2 text-body"
          >
            <Gift size={20} />
            {isOpen && 'Meus Benefícios'}
          </Nav.Link>
        </Nav.Item>
        */}
      </Nav>

      <div className="mt-auto px-2 mb-3">
        <Nav.Item>
          <Nav.Link
            eventKey="darkmode"
            onClick={toggleDarkMode}
            className="d-flex align-items-center gap-2 text-body-secondary"
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            {isOpen && (darkMode ? 'Modo Claro' : 'Modo Noturno')}
          </Nav.Link>
        </Nav.Item>
        <div className="mb-2" />
        <Nav.Item>
          <Nav.Link
            eventKey="logout"
            onClick={() => {
              // recarrega a aplicação (mesmo comportamento da tela de sessão
              // expirada) para descartar todo o estado em memória no logout
              logout().finally(() => window.location.replace('/login'));
            }}
            className="d-flex align-items-center gap-2 text-danger"
          >
            <LogOut size={20} />
            {isOpen && 'Sair'}
          </Nav.Link>
        </Nav.Item>
        {isOpen && (
          <div className="text-center mt-2" style={{ fontSize: '0.7rem', color: '#adb5bd' }}>
            {APP_VERSION}
          </div>
        )}
      </div>
    </div>
  );
}
